import React, { FC, Fragment } from 'react';
import { formatDate } from 'utils/format';
import { TableCell, TableRow, Button, Chip, Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ActiveIndicator from 'components/users/shared/active-indicator/active-indicator.component';
import FormattedAddress from 'components/shared/address/formatted-address.component';
import { Clinic } from 'models/clinic';
import { useNavigate } from 'react-router-dom';

type ClinicItemProps = {
    clinic: Clinic;
    onEditClinicClick: (clinic: Clinic) => void;
    onHHSyncClick?: (clinic: Clinic) => void;
    onAddChildClinicClick?: (clinic: Clinic) => void;
};
const ClinicItem: FC<ClinicItemProps> = ({
    clinic,
    onEditClinicClick,
    onHHSyncClick,
    onAddChildClinicClick,
}: ClinicItemProps) => {
    const { t: translate } = useTranslation(['common', 'organization', 'client-directory']);
    const navigate = useNavigate();
    const getClinicSubLabel = (clinic: Clinic): string => {
        if (!clinic.parentClinicId && !clinic.local && !clinic.isWhiteLabeled) {
            return translate(`client-directory:DENTHUB_CONNECTED`);
        }
        if (clinic.isWhiteLabeled) {
            return translate('client-directory:WHITE_LABEL');
        }
        if (clinic.local) {
            return translate('client-directory:MY_CLIENT');
        }

        if (clinic.parentClinicId && !clinic.local && !clinic.isWhiteLabeled) {
            return translate(`client-directory:DENTHUB_CONNECTED`);
        }
        return '';
    };

    // A main organization is a Denthub customer and connected customer(while label) with no parent of its own. Child clinics are
    // excluded so the hierarchy stays one level deep, and not-connected customers(local) are excluded because
    // they are created through addLocalClinic rather than addClinic.
    const isMainOrganization = !clinic.parentClinicId && !clinic.local;

    const getLabelForMainOrganizationChip = (isParent: boolean): string => {
        if (!isParent) {
            return translate('organization:MAIN_ORGANIZATION');
        }
        return clinic.parentClinic?.name || '';
    };

    return (
        <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => onEditClinicClick(clinic)}>
            <TableCell sx={{ width: 10 }} align="center">
                <ActiveIndicator isActive={clinic.active} />
            </TableCell>
            <TableCell sx={{ width: 400 }}>
                <Box>{clinic.name}</Box>
                <Typography variant="caption" color="grey.500">
                    {clinic.officialName}
                </Typography>
            </TableCell>
            <TableCell>
                <Box>{clinic.organizationNumber}</Box>
                <Typography variant="caption" color="grey.500">
                    {clinic.hitHorizonsId}
                </Typography>
                {clinic.invoicing?.costCenter && (
                    <Typography variant="caption" color="grey.500" display="block">
                        {translate('organization:INVOICE:COST_CENTER')}: {clinic.invoicing.costCenter}
                    </Typography>
                )}
            </TableCell>
            <TableCell>
                <Chip size="small" sx={{ cursor: 'pointer' }} label={getClinicSubLabel(clinic)} />
            </TableCell>
            <TableCell>
                <FormattedAddress address={clinic.contact?.address} oneLineFormat />
            </TableCell>
            <TableCell>{clinic.created?.on && <div>{formatDate(new Date(clinic.created.on), 'date')}</div>}</TableCell>
            <Fragment>
                <TableCell>
                    <Chip
                        size="small"
                        sx={{ cursor: 'pointer' }}
                        label={getLabelForMainOrganizationChip(!!clinic.parentClinicId)}
                    />
                </TableCell>
                <TableCell>
                    <Box display="flex" justifyContent="flex-end">
                        <Button
                            sx={{ marginRight: 1 }}
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (onHHSyncClick) {
                                    onHHSyncClick(clinic);
                                }
                            }}
                        >
                            {translate('organization:HIT_HORIZONS')}
                        </Button>
                        {isMainOrganization && onAddChildClinicClick && (
                            <Button
                                sx={{ marginRight: 1 }}
                                size="small"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onAddChildClinicClick(clinic);
                                }}
                            >
                                {translate('organization:ADD_CHILD_CLINIC')}
                            </Button>
                        )}
                        <Button
                            sx={{ marginRight: 1 }}
                            size="small"
                            onClick={(event) => {
                                event.stopPropagation();
                                navigate(
                                    `/orders?customer=${clinic._id}&customerName=${encodeURIComponent(clinic.name ?? '')}`,
                                );
                            }}
                            data-test="clinic-item.orders"
                        >
                            {translate('super-admin:MENU_ORDERS')}
                        </Button>
                        <Button
                            sx={{ marginRight: 1 }}
                            size="small"
                            onClick={(event) => {
                                event.stopPropagation();
                                navigate(
                                    `/invoices?customer=${clinic._id}&customerName=${encodeURIComponent(clinic.name ?? '')}`,
                                );
                            }}
                            data-test="clinic-item.invoices"
                        >
                            {translate('super-admin:MENU_INVOICES')}
                        </Button>
                        <Button
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/customer-settings/${clinic._id}`);
                            }}
                            size="small"
                        >
                            {translate('common:SETTINGS')}
                        </Button>
                    </Box>
                </TableCell>
            </Fragment>
        </TableRow>
    );
};

export default ClinicItem;
